import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings, TIMEZONE_OPTIONS, DATE_FORMAT_OPTIONS, TIME_FORMAT_OPTIONS } from '../context/SettingsContext';
import { assetsApi, webhooksApi, integrationApi, notificationsApi, tenantSettingsApi, allocateApi, billingApi, usersApi, errorCodeLibrariesApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ReadinessSettings from '../components/settings/ReadinessSettings';
import { SecurityActivityCard } from '../components/settings/SecurityActivityCard';

// Shows the company time zone the server uses for the 05:00 digest.
const DigestTimezoneNote = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => { tenantSettingsApi.getRegional().then(r => setInfo(r.data)).catch(() => {}); }, []);
  if (!info) return null;
  const text = info.source === 'set'
    ? `Digest time zone: ${info.timezone}`
    : info.source === 'country'
      ? `Digest time zone: ${info.timezone} (default for ${info.country}). Choose a time zone above to confirm it.`
      : 'No company time zone or country set, so the digest uses UTC. Choose a time zone above.';
  return <p className="text-[11.5px] mt-1.5" style={{ color: info.source === 'set' ? '#8A857A' : '#92600A' }} data-testid="digest-timezone-note">{text}</p>;
};
import { Badge } from '../components/ui/badge';
import { ErrorCodeLibrariesCard } from '../components/ErrorCodeLibrariesCard';
import { VDaqPanel } from '../components/VDaqPanel';
import BillingPage from './BillingPage';
import { 
  Settings,
  Globe,
  Building2,
  Clock,
  Calendar,
  Save,
  Loader2,
  Upload,
  Image,
  QrCode,
  RefreshCw,
  Cpu,
  Database,
  Plus,
  Trash2,
  Edit,
  Key,
  Webhook,
  Copy,
  CheckCircle,
  X,
  ExternalLink,
  Bell,
  Mail,
  Link,
  HardDrive,
  CreditCard,
  Check,
  ShieldCheck,
  ChevronRight,
  AlertTriangle,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { useConfirm } from '../components/useConfirm';

// ── Gold-standard building blocks ────────────────────────────────────
const SCard = ({ title, desc, headerRight, footer, secondary, children, testId }) => (
  <div
    className="bg-white overflow-hidden"
    style={{ border: '1px solid #DAD5CA', borderRadius: 12, boxShadow: '0 1px 2px rgba(26,26,26,0.05), 0 8px 20px -14px rgba(26,26,26,0.16)' }}
    data-testid={testId}
  >
    {(title || headerRight) && (
      <div className="flex items-start justify-between gap-3" style={{ padding: '14px 16px 12px', borderBottom: '1px solid #E6E2D9' }}>
        <div className="min-w-0">
          <h2 className="text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: secondary ? 14 : 17, letterSpacing: '-0.01em' }}>{title}</h2>
          {desc && <p className="mt-0.5" style={{ fontSize: 12.5, color: '#6B6660' }}>{desc}</p>}
        </div>
        {headerRight && <div className="shrink-0">{headerRight}</div>}
      </div>
    )}
    {children}
    {footer && (
      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ padding: '12px 16px', background: '#FBFAF7', borderTop: '1px solid #E6E2D9' }}>
        {footer}
      </div>
    )}
  </div>
);

const DefRow = ({ label, help, children, last }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-2" style={{ padding: '14px 16px', borderBottom: last ? 'none' : '1px solid #F0EDE6' }}>
    <div style={{ flex: '1 1 260px', minWidth: 0 }}>
      <div className="text-[#1A1A1A]" style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
      {help && <div style={{ fontSize: 12.5, color: '#6B6660' }}>{help}</div>}
    </div>
    <div className="flex justify-end" style={{ flex: '0 1 320px' }}>{children}</div>
  </div>
);

const FieldLabel = ({ children }) => (
  <label className="block uppercase" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', color: '#6B6660', marginBottom: 6 }}>{children}</label>
);

const utcOffsetOf = (tz) => {
  try {
    const s = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';
    const m = s.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/);
    if (!m) return '+00:00';
    const h = m[1].replace('+', '+').padStart(3, '0').replace('+0', '+').replace('-0', '-');
    return `${m[1].startsWith('-') ? '-' : '+'}${String(Math.abs(parseInt(m[1], 10))).padStart(2, '0')}:${m[2] || '00'}`;
  } catch { return '+00:00'; }
};


export const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const { settings, updateSettings, formatDate, formatDateTime } = useSettings();
  const { confirm, dialogProps } = useConfirm();
  const [saving, setSaving] = useState(false);
  const [regeneratingQR, setRegeneratingQR] = useState(false);
  const [publicQrEnabled, setPublicQrEnabled] = useState(false);
  const [publicQrLoading, setPublicQrLoading] = useState(false);
  const [companyData, setCompanyData] = useState({
    companyName: settings.companyName || '',
    companyAddress: settings.companyAddress || '',
    companyPhone: settings.companyPhone || '',
    companyEmail: settings.companyEmail || '',
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Controlled active-tab so we can deep-link via ?tab=billing (and so the
  // /settings/billing legacy route can land directly on the Billing tab).
  // We keep the URL in sync on every tab change so the back button works.
  const VALID_TABS = ['regional', 'company', 'integrations', 'notifications', 'readiness', 'error-codes', 'billing', 'security', 'system'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'regional'
  );
  useEffect(() => {
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl]);
  const handleTabChange = (next) => {
    setActiveTab(next);
    setSearchParams(next === 'regional' ? {} : { tab: next }, { replace: true });
  };

  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDays, setNewKeyDays] = useState(365);
  const [createdKey, setCreatedKey] = useState(null);
  const [creatingKey, setCreatingKey] = useState(false);

  // Webhooks state
  const [webhooks, setWebhooks] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [newWebhook, setNewWebhook] = useState({ name: '', url: '', events: [], secret: '' });
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(null);

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Platform integrations state
  const [platformIntegrations, setPlatformIntegrations] = useState({
    equipr_allocate_enabled: false,
    equipr_core_enabled: false
  });
  const [loadingPlatformIntegrations, setLoadingPlatformIntegrations] = useState(true);
  const [savingPlatformIntegrations, setSavingPlatformIntegrations] = useState(false);
  const [allocateInfo, setAllocateInfo] = useState(null);
  const [togglingAllocate, setTogglingAllocate] = useState(false);

  // Storage usage state
  const [storageUsage, setStorageUsage] = useState(null);
  const [loadingStorage, setLoadingStorage] = useState(true);

  // Rail summary + billing bands
  const [billingSummary, setBillingSummary] = useState(null);
  const [seatUsage, setSeatUsage] = useState(null);
  const [ecLibraries, setEcLibraries] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [showWebhookForm, setShowWebhookForm] = useState(false);

  // Company details from API
  const [loadingCompany, setLoadingCompany] = useState(true);

  useEffect(() => {
    if (isAdmin()) {
      loadApiKeys();
      loadWebhooks();
      loadNotificationSettings();
      loadPlatformIntegrations();
      loadStorageUsage();
      loadCompanyDetails();
      loadPublicQrSetting();
      loadBillingSummary();
      loadErrorCodeCount();
    }
  }, []);

  const loadBillingSummary = async () => {
    try {
      const [bill, seats] = await Promise.all([
        billingApi.me(),
        usersApi.getSeatUsage().catch(() => ({ data: null })),
      ]);
      setBillingSummary(bill.data);
      setSeatUsage(seats.data);
    } catch (error) {
      console.error('Failed to load billing summary:', error);
    }
  };

  const loadErrorCodeCount = async () => {
    try {
      const res = await errorCodeLibrariesApi.list();
      setEcLibraries(Array.isArray(res.data) ? res.data : (res.data?.libraries || []));
    } catch (error) {
      console.error('Failed to load error-code libraries:', error);
    }
  };

  const handleOpenBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await billingApi.portal(window.location.origin);
      if (res.data?.url) window.location.href = res.data.url;
      else toast.error('No portal URL returned');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not open the billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const loadPublicQrSetting = async () => {
    try {
      const response = await tenantSettingsApi.getPublicQR();
      setPublicQrEnabled(response.data.public_qr_enabled);
    } catch (error) {
      console.error('Failed to load public QR setting:', error);
    }
  };

  const togglePublicQr = async (enabled) => {
    setPublicQrLoading(true);
    try {
      const response = await tenantSettingsApi.updatePublicQR({ public_qr_enabled: enabled });
      setPublicQrEnabled(response.data.public_qr_enabled);
      toast.success(enabled ? 'Public QR access enabled' : 'Public QR access disabled');
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setPublicQrLoading(false);
    }
  };

  const loadApiKeys = async () => {
    try {
      const response = await integrationApi.listApiKeys();
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    }
  };

  const loadWebhooks = async () => {
    try {
      const [webhooksRes, eventsRes] = await Promise.all([
        webhooksApi.list(),
        webhooksApi.listEvents()
      ]);
      setWebhooks(webhooksRes.data);
      setWebhookEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const response = await notificationsApi.getSettings();
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }
  };

  const loadStorageUsage = async () => {
    try {
      const response = await tenantSettingsApi.getStorage();
      setStorageUsage(response.data);
    } catch (error) {
      console.error('Failed to load storage usage:', error);
    } finally {
      setLoadingStorage(false);
    }
  };

  const loadCompanyDetails = async () => {
    try {
      const response = await tenantSettingsApi.getCompany();
      const data = response.data;
      setCompanyData({
        companyName: data.company_name || '',
        companyAddress: data.company_address || '',
        companyPhone: data.company_phone || '',
        companyEmail: data.company_email || '',
      });
      if (data.company_logo) updateSettings({ companyLogo: data.company_logo });
    } catch (error) {
      console.error('Failed to load company details:', error);
    } finally {
      setLoadingCompany(false);
    }
  };

  if (!isAdmin()) {
    navigate('/dashboard');
    return null;
  }

  const handleTimezoneChange = (timezone) => {
    updateSettings({ timezone });
    tenantSettingsApi.setRegional({ timezone })
      .then(() => toast.success('Timezone updated. The 5am readiness digest will use it'))
      .catch(() => toast.error('Could not save the company time zone'));
  };

  const handleDateFormatChange = (dateFormat) => {
    updateSettings({ dateFormat });
    toast.success('Date format updated');
  };

  const handleTimeFormatChange = (timeFormat) => {
    updateSettings({ timeFormat });
    toast.success('Time format updated');
  };

  const handleCompanySave = async () => {
    setSaving(true);
    try {
      // Save to backend API
      await tenantSettingsApi.updateCompany({
        company_name: companyData.companyName,
        company_address: companyData.companyAddress,
        company_phone: companyData.companyPhone,
        company_email: companyData.companyEmail,
        company_logo: settings.companyLogo || null,
      });
      // Also update local settings for display
      updateSettings(companyData);
      toast.success('Company details saved');
    } catch (error) {
      console.error('Failed to save company details:', error);
      toast.error(error.response?.data?.detail || 'Failed to save company details');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateQRCodes = () => {
    confirm({
      title: 'Regenerate all QR codes?',
      description: 'Every asset gets a fresh code in the current URL format. Codes already printed keep working.',
      confirmLabel: 'Regenerate',
      testId: 'confirm-regenerate-qr',
      onConfirm: async () => {
        setRegeneratingQR(true);
        try {
          const response = await assetsApi.regenerateQRCodes();
          toast.success(response.data.message || 'QR codes regenerated successfully');
        } catch (error) {
          toast.error('Failed to regenerate QR codes');
          throw error;
        } finally {
          setRegeneratingQR(false);
        }
      },
    });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = async () => {
        updateSettings({ companyLogo: reader.result });
        try {
          await tenantSettingsApi.updateCompany({ company_logo: reader.result });
          toast.success('Logo uploaded');
        } catch (err) {
          toast.error('Logo saved for preview but failed to sync to the server');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // API Key handlers
  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Key name is required');
      return;
    }
    setCreatingKey(true);
    try {
      const response = await integrationApi.createApiKey({
        name: newKeyName.trim(),
        expires_in_days: newKeyDays
      });
      setCreatedKey(response.data);
      setApiKeys([...apiKeys, {
        id: response.data.id,
        name: response.data.name,
        key_prefix: response.data.key_prefix,
        created_at: response.data.created_at,
        expires_at: response.data.expires_at,
        is_active: true,
        last_used_at: null
      }]);
      setNewKeyName('');
      toast.success('API key created');
    } catch (error) {
      toast.error('Failed to create API key');
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeApiKey = (keyId, keyName) => {
    confirm({
      title: 'Revoke this API key?',
      description: `"${keyName}" will stop working immediately.`,
      footnote: 'This cannot be undone. Any integration using this key will lose access.',
      confirmLabel: 'Revoke Key',
      testId: 'confirm-revoke-api-key',
      onConfirm: async () => {
        try {
          await integrationApi.revokeApiKey(keyId);
          setApiKeys(apiKeys.map(k => k.id === keyId ? { ...k, is_active: false } : k));
          toast.success('API key revoked');
        } catch (error) {
          toast.error('Failed to revoke API key');
          throw error;
        }
      },
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Webhook handlers
  const handleCreateWebhook = async () => {
    if (!newWebhook.name.trim() || !newWebhook.url.trim() || newWebhook.events.length === 0) {
      toast.error('Name, URL, and at least one event are required');
      return;
    }
    if (!newWebhook.url.startsWith('https://')) {
      toast.error('Webhook URL must use HTTPS');
      return;
    }
    setCreatingWebhook(true);
    try {
      const response = await webhooksApi.create(newWebhook);
      setWebhooks([...webhooks, response.data]);
      setNewWebhook({ name: '', url: '', events: [], secret: '' });
      toast.success('Webhook created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    } finally {
      setCreatingWebhook(false);
    }
  };

  const handleDeleteWebhook = (webhookId) => {
    confirm({
      title: 'Delete this webhook?',
      description: 'Guardian will stop sending events to this endpoint.',
      confirmLabel: 'Delete Webhook',
      testId: 'confirm-delete-webhook',
      onConfirm: async () => {
        try {
          await webhooksApi.delete(webhookId);
          setWebhooks(webhooks.filter(w => w.id !== webhookId));
          toast.success('Webhook deleted');
        } catch (error) {
          toast.error('Failed to delete webhook');
          throw error;
        }
      },
    });
  };

  const handleTestWebhook = async (webhookId) => {
    setTestingWebhook(webhookId);
    try {
      const response = await webhooksApi.test(webhookId);
      if (response.data.success) {
        toast.success(`Test successful! Status: ${response.data.status_code}`);
      } else {
        toast.error(`Test failed: ${response.data.error || `Status ${response.data.status_code}`}`);
      }
    } catch (error) {
      toast.error('Failed to test webhook');
    } finally {
      setTestingWebhook(null);
    }
  };

  const toggleWebhookEvent = (event) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  // Notification settings handlers
  const handleSaveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      const res = await notificationsApi.updateSettings(notificationSettings);
      // Backend echoes the canonical saved settings — sync state so the
      // checkboxes immediately reflect what's actually persisted (catches
      // any future field-name drift between frontend & backend models).
      if (res?.data) setNotificationSettings(res.data);
      toast.success('Notification settings saved');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save notification settings');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) return;
    setSendingTestEmail(true);
    try {
      const response = await notificationsApi.sendTestEmail({ recipient_email: testEmail.trim() });
      toast.success(`Test email sent to ${testEmail}`);
      setTestEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Platform integrations handlers
  const loadPlatformIntegrations = async () => {
    try {
      const response = await tenantSettingsApi.getIntegrations();
      setPlatformIntegrations(response.data);
    } catch (error) {
      console.error('Failed to load platform integrations:', error);
    } finally {
      setLoadingPlatformIntegrations(false);
    }
    try {
      const info = await allocateApi.info();
      setAllocateInfo(info.data);
    } catch (error) {
      // Non-fatal — just hide the metadata strip
    }
  };

  // Toggle Allocate via the Pro-gated endpoint so Free tenants get a clear
  // 402 → upgrade redirect instead of silent failure.
  const handleToggleAllocate = async (checked) => {
    setTogglingAllocate(true);
    try {
      if (checked) {
        await allocateApi.enable();
      } else {
        await allocateApi.disable();
      }
      setPlatformIntegrations((p) => ({ ...p, equipr_allocate_enabled: checked }));
      const info = await allocateApi.info();
      setAllocateInfo(info.data);
      toast.success(checked ? 'EQUIPR Allocate integration enabled' : 'EQUIPR Allocate integration disabled');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 402) {
        toast.error('Allocate integration is a Pro feature');
        navigate('/settings/billing');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not toggle Allocate integration');
      }
    } finally {
      setTogglingAllocate(false);
    }
  };

  const handleSavePlatformIntegrations = async () => {
    setSavingPlatformIntegrations(true);
    try {
      // Allocate is toggled via its dedicated endpoint above; only persist Core here.
      await tenantSettingsApi.updateIntegrations({
        equipr_core_enabled: platformIntegrations.equipr_core_enabled,
      });
      toast.success('Platform integrations saved');
    } catch (error) {
      toast.error('Failed to save platform integrations');
    } finally {
      setSavingPlatformIntegrations(false);
    }
  };

  // Get current date for preview
  const now = new Date();

  const primaryBtn = "inline-flex items-center justify-center gap-2 h-[34px] px-3.5 rounded-[7px] text-[12px] font-bold uppercase tracking-[0.04em] bg-[#FFD108] border border-[#E0B800] text-black hover:bg-[#E5B93D] transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const secondaryBtn = "inline-flex items-center justify-center gap-2 h-[34px] px-3.5 rounded-[7px] text-[12.5px] font-semibold bg-white border border-[#C9C3B6] text-[#1A1A1A] hover:border-[#1A1A1A] transition-colors disabled:opacity-60";

  const notifKeys = notificationSettings ? [
    notificationSettings.maintenance_reminders ?? true,
    notificationSettings.auto_create_work_orders_from_pm || false,
    notificationSettings.defect_alerts ?? true,
    notificationSettings.inspection_failures ?? true,
    notificationSettings.gps_silence_alerts ?? true,
    notificationSettings.certificate_alerts || false,
  ] : [];
  const notifOn = notifKeys.filter(Boolean).length;
  const activeKeyCount = apiKeys.filter(k => k.is_active).length;
  const ecCodes = ecLibraries.reduce((s, l) => s + (l.code_count || 0), 0);
  const isPro = billingSummary?.is_pro;
  const planName = isPro ? 'Professional' : 'Free';
  const currency = billingSummary?.currency || 'AUD';
  const money = (c) => `${currency} $${((c || 0) / 100).toFixed(2)}`;
  const proCents = isPro ? (billingSummary?.pro_price?.amount_cents || 0) : 0;
  const gpsCents = (billingSummary?.gps_count || 0) * (billingSummary?.gps_price_cents || 0);
  const seatsUsed = seatUsage?.used ?? 0;
  const seatCents = seatUsage?.projected_monthly_charge_cents ?? 0;
  const totalCents = proCents + gpsCents + seatCents;
  const scanBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const scanUrl = `${scanBase}/scan/{asset_id}`;

  const SECTIONS = [
    { group: 'Workspace', items: [
      { id: 'regional', label: 'Regional', state: `${utcOffsetOf(settings.timezone)} · ${formatDate(now)}` },
      { id: 'company', label: 'Company', state: companyData.companyName || 'Not set' },
      { id: 'billing', label: 'Billing', state: billingSummary ? `${planName} · ${money(totalCents)}` : '—' },
    ]},
    { group: 'Platform', items: [
      { id: 'integrations', label: 'Integrations', state: `${platformIntegrations.equipr_allocate_enabled ? 'Allocate on' : 'Allocate off'} · ${activeKeyCount} key${activeKeyCount === 1 ? '' : 's'}` },
      { id: 'notifications', label: 'Notifications', state: `${notifOn} of ${notifKeys.length || 6} on · email connected` },
      { id: 'readiness', label: 'Readiness', state: 'Traffic-light rules' },
      { id: 'error-codes', label: 'Error Codes', state: ecLibraries.length ? `${ecLibraries.length} librar${ecLibraries.length === 1 ? 'y' : 'ies'} · ${ecCodes} codes` : 'No libraries' },
      { id: 'security', label: 'Security', state: 'Activity log · 60 min idle sign-out' },
      { id: 'system', label: 'System', state: publicQrEnabled ? 'Public QR on' : 'Public QR off' },
    ]},
  ];

  const setPref = (key, val) => setNotificationSettings({ ...notificationSettings, [key]: val });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} className="animate-fade-in" data-testid="settings-page">
      {/* Page title */}
      <div>
        <h1 className="uppercase text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 26, letterSpacing: '-0.015em' }}>Settings</h1>
        <p style={{ fontSize: 12.5, color: '#6B6660' }}>
          Workspace configuration. Changes apply to every user in {companyData.companyName || 'your workspace'} unless the row says otherwise.
        </p>
      </div>

      {/* Body: rail | panel */}
      <div className="grid items-start" style={{ gridTemplateColumns: '268px minmax(0,1fr)', gap: 14 }}>
        {/* Section rail */}
        <div
          className="bg-white sticky self-start hidden lg:block"
          style={{ top: 76, border: '1px solid #DAD5CA', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(26,26,26,0.05), 0 8px 20px -14px rgba(26,26,26,0.16)' }}
        >
          {SECTIONS.map((grp) => (
            <div key={grp.group} className="py-1.5">
              <div className="uppercase" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.14em', color: '#8F8A7F', padding: '6px 14px 4px' }}>{grp.group}</div>
              {grp.items.map((it) => {
                const active = activeTab === it.id;
                return (
                  <button
                    key={it.id}
                    onClick={() => handleTabChange(it.id)}
                    data-testid={`${it.id}-tab`}
                    className="w-full text-left transition-colors"
                    style={{
                      padding: '9px 14px',
                      borderLeft: `3px solid ${active ? '#FFD108' : 'transparent'}`,
                      background: active ? '#FFF8D6' : 'transparent',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: active ? 700 : 500, color: '#1A1A1A' }}>{it.label}</div>
                    <div className="truncate" style={{ fontSize: 11.5, color: '#8F8A7F' }}>{it.state}</div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Mobile rail as a select */}
        <div className="lg:hidden">
          <Select value={activeTab} onValueChange={handleTabChange}>
            <SelectTrigger data-testid="settings-section-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SECTIONS.flatMap(g => g.items).map(it => (
                <SelectItem key={it.id} value={it.id}>{it.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Active panel */}
        <div className="flex flex-col" style={{ gap: 14 }}>
          {/* ── REGIONAL ── */}
          {activeTab === 'regional' && (
            <SCard
              title="Regional"
              desc="How Guardian renders every date, time and timestamp in the web app, exports and PDF reports."
              footer={
                <div className="flex items-center gap-3 flex-wrap font-mono" style={{ fontSize: 13, color: '#1A1A1A' }}>
                  <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#8F8A7F' }}>Reads as</span>
                  <span data-testid="regional-preview-datetime">{formatDateTime(now)}</span>
                  <span style={{ color: '#B5AFA3' }}>date only</span>
                  <span>{formatDate(now)}</span>
                </div>
              }
            >
              <DefRow label="Timezone" help="Also sets when your daily 5am readiness digest is sent. Timestamps are stored in UTC and displayed here.">
                <Select value={settings.timezone} onValueChange={handleTimezoneChange}>
                  <SelectTrigger className="w-full" data-testid="timezone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIMEZONE_OPTIONS.map(tz => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}</SelectContent>
                </Select>
                <DigestTimezoneNote />
              </DefRow>
              <DefRow label="Date format" help="Applies to columns, filters and the date pickers.">
                <Select value={settings.dateFormat} onValueChange={handleDateFormatChange}>
                  <SelectTrigger className="w-full" data-testid="date-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DATE_FORMAT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </DefRow>
              <DefRow label="Time format" help="Mobile inspection logs use the same setting." last>
                <Select value={settings.timeFormat} onValueChange={handleTimeFormatChange}>
                  <SelectTrigger className="w-full" data-testid="time-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIME_FORMAT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </DefRow>
            </SCard>
          )}

          {/* ── COMPANY ── */}
          {activeTab === 'company' && (
            <>
              <SCard
                title="Company details"
                desc="Printed on inspection PDFs, certificates and the public asset QR page."
                footer={
                  <div style={{ fontSize: 12, color: '#6B6660' }}>
                    <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#8F8A7F', marginRight: 8 }}>Appears on documents as</span>
                    {[companyData.companyName, companyData.companyAddress?.replace(/\n/g, ' '), companyData.companyPhone, companyData.companyEmail].filter(Boolean).join(' · ')}
                  </div>
                }
              >
                <div style={{ padding: 16 }}>
                  <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                    <div>
                      <FieldLabel>Company name</FieldLabel>
                      <Input value={companyData.companyName} onChange={(e) => setCompanyData(p => ({ ...p, companyName: e.target.value }))} data-testid="company-name-input" />
                    </div>
                    <div>
                      <FieldLabel>Phone number</FieldLabel>
                      <Input value={companyData.companyPhone} onChange={(e) => setCompanyData(p => ({ ...p, companyPhone: e.target.value }))} data-testid="company-phone-input" />
                    </div>
                    <div>
                      <FieldLabel>Email</FieldLabel>
                      <Input type="email" value={companyData.companyEmail} onChange={(e) => setCompanyData(p => ({ ...p, companyEmail: e.target.value }))} data-testid="company-email-input" />
                    </div>
                    <div>
                      <FieldLabel>Company logo</FieldLabel>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center overflow-hidden" style={{ width: 40, height: 40, borderRadius: 8, background: '#F4F2ED', border: '1px solid #E6E2D9' }}>
                          {settings.companyLogo ? <img src={settings.companyLogo} alt="Logo" className="w-full h-full object-contain" /> : <Image className="w-4 h-4 text-[#B5AFA3]" />}
                        </div>
                        <label className={secondaryBtn + ' cursor-pointer'}>
                          <Upload className="w-3.5 h-3.5" /> Upload logo
                          <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload-input" />
                        </label>
                      </div>
                      <p style={{ fontSize: 11, color: '#A39D91', marginTop: 6 }}>PNG or SVG, min 240px</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <FieldLabel>Address</FieldLabel>
                    <Textarea rows={2} value={companyData.companyAddress} onChange={(e) => setCompanyData(p => ({ ...p, companyAddress: e.target.value }))} data-testid="company-address-input" />
                  </div>
                </div>
                <div className="hidden" />
                {/* Footer save button lives beside the preview text */}
                <div style={{ display: 'none' }} />
              </SCard>
              {/* Save button row appended into the Company footer via a dedicated strip */}
              <div style={{ marginTop: -14 }} />
              <div className="flex justify-end">
                <button onClick={handleCompanySave} disabled={saving} className={primaryBtn} data-testid="save-company-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}{saving ? 'Saving' : 'Save company'}
                </button>
              </div>

              {/* Storage */}
              <SCard title="Storage" secondary desc="Asset photos, defect photos, documents, certificates and inspection attachments.">
                <div style={{ padding: 16 }}>
                  {loadingStorage ? (
                    <div style={{ height: 6, borderRadius: 999, background: '#F0EDE6' }} />
                  ) : storageUsage ? (
                    <>
                      <div className="flex items-center justify-between" style={{ fontSize: 12.5 }}>
                        <span className="font-mono" style={{ color: '#1A1A1A' }}>{storageUsage.storage_used_formatted} of {storageUsage.storage_limit_formatted}</span>
                        <span style={{ color: '#6B6660' }}>{storageUsage.usage_percentage.toFixed(1)}% used</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 999, background: '#F0EDE6', marginTop: 8, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(100, storageUsage.usage_percentage)}%`, background: storageUsage.usage_percentage > 90 ? '#B91C1C' : storageUsage.usage_percentage > 75 ? '#CA8A04' : '#3D7A2E', transition: 'width .3s' }} />
                      </div>
                    </>
                  ) : (
                    <p style={{ fontSize: 12.5, color: '#B91C1C' }}>Couldn't load storage usage.</p>
                  )}
                </div>
              </SCard>
            </>
          )}

          {/* ── BILLING ── */}
          {activeTab === 'billing' && (
            <>
              <SCard testId="billing-current-plan">
                <div className="flex items-center justify-between flex-wrap gap-4" style={{ padding: 16 }}>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 10, background: '#FFF8D6', border: '1px solid #EFE0A0' }}>
                      <ShieldCheck className="w-5 h-5 text-[#8A6D00]" />
                    </div>
                    <div>
                      <div className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#8F8A7F' }}>Current plan</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1A1A1A' }} data-testid="current-plan-name">{planName}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#8F8A7F' }}>Projected this month</div>
                      <div className="font-mono" style={{ fontSize: 18, fontWeight: 700, color: '#1A1A1A' }} data-testid="billing-projected-total">{billingSummary ? money(totalCents) : '—'}</div>
                    </div>
                    {isPro && (
                      <button onClick={handleOpenBillingPortal} disabled={portalLoading} className={secondaryBtn} data-testid="manage-subscription-btn">
                        {portalLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}Manage subscription <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </SCard>

              <SCard
                title="Usage this month"
                headerRight={billingSummary?.billing_period ? <span className="font-mono" style={{ fontSize: 11, color: '#A39D91' }}>Billing period {billingSummary.billing_period}</span> : null}
              >
                <div className="overflow-x-auto">
                  <div style={{ minWidth: 560 }}>
                    <div className="grid items-center uppercase" style={{ gridTemplateColumns: 'minmax(220px,1fr) 120px 120px', padding: '8px 16px', fontSize: 10, letterSpacing: '0.1em', color: '#8F8A7F', background: '#F6F4EF', borderBottom: '1px solid #E6E2D9' }}>
                      <span>Line</span><span className="text-right">Quantity</span><span className="text-right">Amount</span>
                    </div>
                    {[
                      { label: isPro ? 'Pro subscription' : 'Free plan', help: isPro ? 'Unlimited assets, Guardian AI, bulk PM scheduling, priority support' : 'Up to 10 assets, core registers', qty: isPro ? '1 workspace' : '—', amt: money(proCents) },
                      { label: 'GPS telemetry', help: `${money(billingSummary?.gps_price_cents || 0)} per asset per month, billed as GPS is enabled on an asset`, qty: `${billingSummary?.gps_count || 0} assets`, amt: money(gpsCents) },
                      { label: 'Office user seats', help: 'Admin and Fleet Manager seats. Mechanics and mobile field users are unlimited and free.', qty: `${seatsUsed} seat${seatsUsed === 1 ? '' : 's'}`, amt: money(seatCents) },
                    ].map((r, i) => (
                      <div key={i} className="grid items-center" style={{ gridTemplateColumns: 'minmax(220px,1fr) 120px 120px', padding: '12px 16px', borderBottom: '1px solid #F0EDE6' }}>
                        <div style={{ paddingRight: 12 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{r.label}</div>
                          <div style={{ fontSize: 12, color: '#6B6660' }}>{r.help}</div>
                        </div>
                        <div className="text-right font-mono" style={{ fontSize: 12.5, color: '#6B6660' }}>{r.qty}</div>
                        <div className="text-right font-mono" style={{ fontSize: 13, color: '#1A1A1A' }}>{r.amt}</div>
                      </div>
                    ))}
                    <div className="grid items-center" style={{ gridTemplateColumns: 'minmax(220px,1fr) 120px 120px', padding: '12px 16px', background: '#FBFAF7' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A' }}>Total {currency}</div>
                      <div />
                      <div className="text-right font-mono" style={{ fontSize: 14, fontWeight: 700, color: '#1A1A1A' }} data-testid="billing-total">{money(totalCents)}</div>
                    </div>
                  </div>
                </div>
              </SCard>

              <SCard
                title="Plans"
                headerRight={isPro ? <button onClick={handleOpenBillingPortal} className="text-[#6B6660] hover:text-[#1A1A1A]" style={{ fontSize: 12.5 }} data-testid="downgrade-cta">Downgrade to Free</button> : null}
              >
                <div className="grid gap-0" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px,1fr))' }}>
                  {[
                    { name: 'Free', price: '$0', cur: !isPro, lines: ['Up to 10 assets, 5 office seats, unlimited mechanics', 'Asset register and QR scanning', 'Inspections, defects and work orders', 'Preventative maintenance scheduling', 'Compliance and certificate register'] },
                    { name: 'Pro', price: `${currency} ${money(billingSummary?.pro_price?.amount_cents || 3900).replace(currency + ' ', '')}`, cur: isPro, lines: ['Everything in Free, with unlimited assets', 'Guardian AI — chat, work-order assistant and insights', 'Telemetry and GPS at $1.99 per asset per month', 'Bulk PM scheduling and Task Library seeding', 'Email and in-app notifications, priority support'] },
                  ].map((p, i) => (
                    <div key={p.name} style={{ padding: 16, borderLeft: i === 1 ? '1px solid #F0EDE6' : 'none', background: p.cur ? '#FFFCEF' : 'transparent' }}>
                      <div className="flex items-baseline justify-between">
                        <div className="flex items-baseline gap-2">
                          <span style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A' }}>{p.name}</span>
                          <span className="font-mono" style={{ fontSize: 12.5, color: '#6B6660' }}>{p.price} <span style={{ color: '#A39D91' }}>/month</span></span>
                        </div>
                        {p.cur && <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#8A6D00' }}>Current</span>}
                      </div>
                      <ul className="mt-3 space-y-1.5">
                        {p.lines.map((l, j) => <li key={j} style={{ fontSize: 12.5, color: '#3D3A35' }}>{l}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              </SCard>
            </>
          )}

          {/* ── INTEGRATIONS ── */}
          {activeTab === 'integrations' && (
            <>
              <SCard title="V-DAQ telemetry" desc="Connect your V-DAQ account so linked vehicles stream live location and engine hours into Guardian." testId="integration-card-vdaq">
                <div className="p-4"><VDaqPanel /></div>
              </SCard>

              <SCard title="EQUIPR platform" desc="Connect Guardian to the rest of the EQUIPR suite so the asset register stays in one place." testId="integration-card-allocate">
                {loadingPlatformIntegrations ? (
                  <div style={{ padding: 16 }}><div style={{ height: 6, borderRadius: 999, background: '#F0EDE6' }} /></div>
                ) : (
                  <>
                    <DefRow
                      label={<span className="flex items-center gap-2">EQUIPR Allocate <span className="uppercase" style={{ fontSize: 9, fontWeight: 700, background: '#FFF8D6', color: '#8A6D00', border: '1px solid #E0B800', padding: '1px 5px', borderRadius: 4 }}>Pro</span></span>}
                      help="Sync the asset register from Allocate and deep-link back into it."
                      last={!platformIntegrations.equipr_allocate_enabled}
                    >
                      <Switch checked={platformIntegrations.equipr_allocate_enabled} disabled={togglingAllocate} onCheckedChange={handleToggleAllocate} data-testid="equipr-allocate-switch" />
                    </DefRow>
                    {platformIntegrations.equipr_allocate_enabled && allocateInfo && (
                      <div style={{ background: '#FFFCEF', borderBottom: '1px solid #EFE0A0', borderTop: '1px solid #EFE0A0', padding: '14px 16px' }} data-testid="allocate-info-panel">
                        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))' }}>
                          <div><div className="uppercase" style={{ fontSize: 10, letterSpacing: '0.12em', color: '#8F8A7F', fontWeight: 700 }}>Assets synced</div><div className="font-mono" style={{ fontSize: 13 }}>{allocateInfo.synced_count}</div></div>
                          <div><div className="uppercase" style={{ fontSize: 10, letterSpacing: '0.12em', color: '#8F8A7F', fontWeight: 700 }}>Expected SP version</div><div className="font-mono" style={{ fontSize: 13 }}>{allocateInfo.expected_sp_version}</div></div>
                          {allocateInfo.last_sync_at && <div><div className="uppercase" style={{ fontSize: 10, letterSpacing: '0.12em', color: '#8F8A7F', fontWeight: 700 }}>Last sync</div><div className="font-mono" style={{ fontSize: 13 }}>{formatDateTime(allocateInfo.last_sync_at)}</div></div>}
                        </div>
                        <div className="mt-3">
                          <div className="uppercase" style={{ fontSize: 10, letterSpacing: '0.12em', color: '#8F8A7F', fontWeight: 700, marginBottom: 4 }}>Deep-link pattern — paste into Allocate</div>
                          <div className="flex gap-2">
                            <Input readOnly value={allocateInfo.deep_link_pattern} className="font-mono text-xs" style={{ minWidth: 0 }} data-testid="allocate-deeplink-input" />
                            <button className={secondaryBtn} onClick={() => { navigator.clipboard.writeText(allocateInfo.deep_link_pattern); toast.success('Deep-link pattern copied'); }} data-testid="allocate-copy-deeplink"><Copy className="w-3.5 h-3.5" />Copy</button>
                          </div>
                          <p style={{ fontSize: 11, color: '#8F8A7F', marginTop: 6 }}>example · <span className="font-mono">{allocateInfo.deep_link_example}</span>{allocateInfo.client_id && <> · Client ID: <span className="font-mono" style={{ color: '#3D3A35' }}>{allocateInfo.client_id}</span></>}</p>
                          <div className="flex gap-2 mt-2">
                            <button className={secondaryBtn} onClick={() => navigate('/assets/import')} data-testid="allocate-import-cta">Import from Allocate</button>
                            <button className="text-[#8A6D00] hover:text-[#6B5500]" style={{ fontSize: 12.5, fontWeight: 600 }} onClick={() => navigate('/help/integrations-allocate')} data-testid="allocate-docs-cta">Read the integration guide</button>
                          </div>
                        </div>
                      </div>
                    )}
                    <DefRow label={<span className="flex items-center gap-2">EQUIPR Core <span className="uppercase" style={{ fontSize: 9, fontWeight: 700, background: '#F1EFEA', color: '#8F8A7F', border: '1px solid #E6E2D9', padding: '1px 5px', borderRadius: 4 }}>Coming soon</span></span>} help="Not connected. Connecting shares the customer and site register with Guardian." last>
                      <Switch checked={false} disabled data-testid="equipr-core-switch" />
                    </DefRow>
                  </>
                )}
              </SCard>

              {/* One-time key reveal — blocking, copy-first */}
              {createdKey && (
                <div style={{ background: '#FFFCEF', border: '1px solid #EFE0A0', borderRadius: 12, padding: 16 }} data-testid="created-key-panel">
                  <div className="flex items-center gap-2" style={{ color: '#8A6D00', fontWeight: 700, fontSize: 13 }}><CheckCircle className="w-4 h-4" />API key created — copy it now, it won't be shown again</div>
                  <div className="flex gap-2 mt-3">
                    <Input readOnly value={createdKey.api_key} className="font-mono text-sm" data-testid="created-api-key-value" />
                    <button className={secondaryBtn} onClick={() => copyToClipboard(createdKey.api_key)}><Copy className="w-3.5 h-3.5" />Copy</button>
                  </div>
                  <div className="flex justify-end mt-3"><button className={primaryBtn} onClick={() => setCreatedKey(null)} data-testid="dismiss-created-key">I've saved it</button></div>
                </div>
              )}

              {/* API keys */}
              <SCard
                title="API keys"
                headerRight={<button className={secondaryBtn} onClick={() => setShowKeyForm(s => !s)} data-testid="new-key-btn"><Plus className="w-3.5 h-3.5" />New key</button>}
                desc={`${activeKeyCount} active · ${apiKeys.length - activeKeyCount} revoked`}
              >
                <p className="text-[12.5px]" style={{ padding: '10px 16px', background: '#FFF8E6', borderBottom: '1px solid #F0EDE6', color: '#6B5A2E' }} data-testid="api-keys-ai-note">
                  These keys are for your own integrations (Integration API). They don't work with EQUIPR AI. EQUIPR AI keys are issued by EQUIPR support.
                </p>
                {showKeyForm && (
                  <div className="flex flex-wrap items-end gap-3" style={{ padding: '14px 16px', background: '#FBFAF7', borderBottom: '1px solid #F0EDE6' }}>
                    <div style={{ flex: '1 1 220px' }}><FieldLabel>Key name</FieldLabel><Input placeholder="e.g. Scheduling Platform" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} data-testid="api-key-name-input" /></div>
                    <div style={{ flex: '0 0 120px' }}><FieldLabel>Expires (days)</FieldLabel><Input type="number" min={1} max={730} value={newKeyDays} onChange={(e) => setNewKeyDays(parseInt(e.target.value) || 365)} data-testid="api-key-expiry-input" /></div>
                    <button className={primaryBtn} disabled={creatingKey || !newKeyName.trim()} onClick={handleCreateApiKey} data-testid="create-api-key-btn">{creatingKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}Generate</button>
                  </div>
                )}
                {apiKeys.length === 0 ? (
                  <div style={{ padding: '28px 16px', textAlign: 'center', color: '#8F8A7F', fontSize: 13 }}>No API keys yet. Create one to integrate an external system.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <div style={{ minWidth: 720 }}>
                      <div className="grid items-center uppercase" style={{ gridTemplateColumns: 'minmax(180px,1.3fr) minmax(160px,1fr) 130px 100px 90px', padding: '8px 16px', fontSize: 10, letterSpacing: '0.1em', color: '#8F8A7F', background: '#F6F4EF', borderBottom: '1px solid #E6E2D9' }}>
                        <span>Name</span><span>Key</span><span>Expires</span><span>Status</span><span />
                      </div>
                      {apiKeys.map(k => (
                        <div key={k.id} className="grid items-center" style={{ gridTemplateColumns: 'minmax(180px,1.3fr) minmax(160px,1fr) 130px 100px 90px', padding: '11px 16px', borderBottom: '1px solid #F0EDE6', opacity: k.is_active ? 1 : 0.6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: k.is_active ? '#1A1A1A' : '#8F8A7F' }}>{k.name}</span>
                          <span className="font-mono truncate" style={{ fontSize: 11.5, color: k.is_active ? '#6B6660' : '#B5AFA3' }}>{k.key_prefix}········</span>
                          <span className="font-mono" style={{ fontSize: 11.5, color: k.is_active ? '#6B6660' : '#B5AFA3' }}>{formatDate(k.expires_at)}</span>
                          <span>
                            {k.is_active
                              ? <span className="inline-flex items-center gap-1" style={{ fontSize: 11, fontWeight: 600, color: '#3D7A2E', background: '#EAF4E5', border: '1px solid #CDE6C2', padding: '1px 7px', borderRadius: 999 }}>Active</span>
                              : <span style={{ fontSize: 11.5, color: '#B5AFA3' }}>Revoked</span>}
                          </span>
                          <span className="text-right">
                            {k.is_active && <button className="hover:underline" style={{ fontSize: 12, color: '#B91C1C' }} onClick={() => handleRevokeApiKey(k.id, k.name)}>Revoke</button>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </SCard>

              {/* Webhooks */}
              <SCard
                title="Webhooks"
                desc={`${webhooks.length} endpoint${webhooks.length === 1 ? '' : 's'}`}
                headerRight={<button className={secondaryBtn} onClick={() => setShowWebhookForm(s => !s)} data-testid="new-webhook-btn"><Plus className="w-3.5 h-3.5" />New webhook</button>}
              >
                {[...webhooks].sort((a, b) => (b.failure_count || 0) - (a.failure_count || 0)).map(w => {
                  const failing = (w.failure_count || 0) >= 5;
                  return (
                    <div key={w.id} className="flex items-start justify-between gap-3" style={{ padding: '12px 16px', borderBottom: '1px solid #F0EDE6', background: failing ? '#FEF2F2' : 'transparent' }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="flex items-center gap-2">
                          {failing && <AlertTriangle className="w-4 h-4 text-[#B91C1C]" />}
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{w.name}</span>
                          {failing && <span style={{ fontSize: 11.5, color: '#B91C1C', fontWeight: 600 }}>— {w.failure_count} consecutive failures</span>}
                        </div>
                        <div className="font-mono truncate" style={{ fontSize: 11.5, color: failing ? '#B91C1C' : '#6B6660', overflowWrap: 'anywhere' }}>{w.url}</div>
                        <div className="flex flex-wrap gap-1 mt-1.5">{(w.events || []).map(e => <span key={e} className="font-mono" style={{ fontSize: 10.5, color: '#5A5650', background: '#FFF', border: '1px solid #C9C3B6', padding: '1px 7px', borderRadius: 999 }}>{e}</span>)}</div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button className={secondaryBtn + ' !h-8 !px-2'} onClick={() => handleTestWebhook(w.id)} disabled={testingWebhook === w.id} title="Send test">{testingWebhook === w.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}</button>
                        <button className="hover:underline self-center" style={{ fontSize: 12, color: '#B91C1C' }} onClick={() => handleDeleteWebhook(w.id)} data-testid={`delete-webhook-${w.id}`}>Delete</button>
                      </div>
                    </div>
                  );
                })}
                {(showWebhookForm || webhooks.length === 0) && (
                  <div style={{ padding: 16 }}>
                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px,1fr))' }}>
                      <div><FieldLabel>Webhook name</FieldLabel><Input placeholder="e.g. Slack Notifications" value={newWebhook.name} onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })} data-testid="webhook-name-input" /></div>
                      <div><FieldLabel>URL — HTTPS required</FieldLabel><Input placeholder="https://your-endpoint.com/webhook" value={newWebhook.url} onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })} data-testid="webhook-url-input" /></div>
                    </div>
                    <div className="mt-3">
                      <FieldLabel>Events — {newWebhook.events.length} of {webhookEvents.length} selected</FieldLabel>
                      <div className="flex flex-wrap gap-2">
                        {webhookEvents.map(ev => {
                          const on = newWebhook.events.includes(ev);
                          return (
                            <button key={ev} onClick={() => toggleWebhookEvent(ev)} className="font-mono transition-colors" data-testid={`webhook-event-${ev}`}
                              style={{ height: 28, padding: '0 12px', borderRadius: 999, fontSize: 11, border: `1px solid ${on ? '#E0B800' : '#C9C3B6'}`, background: on ? '#FFF8D6' : '#FFFFFF', color: on ? '#8A6D00' : '#5A5650' }}>{ev}</button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex justify-start mt-3">
                      <button className={primaryBtn} disabled={creatingWebhook || !newWebhook.name.trim() || !newWebhook.url.trim() || newWebhook.events.length === 0} onClick={handleCreateWebhook} data-testid="create-webhook-btn">{creatingWebhook ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Webhook className="w-3.5 h-3.5" />}Create webhook</button>
                    </div>
                  </div>
                )}
              </SCard>
            </>
          )}

          {/* ── NOTIFICATIONS ── */}
          {activeTab === 'notifications' && (
            <>
              <SCard
                title="Notification preferences"
                desc="Which events raise an alert in the inbox and, where enabled, an email."
                headerRight={<span style={{ fontSize: 12.5, color: '#8F8A7F' }} data-testid="notif-on-count">{notifOn} of {notifKeys.length || 6} on</span>}
                footer={<div className="ml-auto"><button className={primaryBtn} onClick={handleSaveNotificationSettings} disabled={savingNotifications} data-testid="save-preferences-btn">{savingNotifications ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}Save preferences</button></div>}
              >
                {!notificationSettings ? (
                  <div style={{ padding: 16 }}><div style={{ height: 6, borderRadius: 999, background: '#F0EDE6' }} /></div>
                ) : (
                  <>
                    <DefRow label="Maintenance due alerts" help="When an asset is approaching a scheduled service interval.">
                      <Switch checked={notificationSettings.maintenance_reminders ?? true} onCheckedChange={(c) => setPref('maintenance_reminders', c)} data-testid="notif-maintenance-reminders-switch" />
                    </DefRow>
                    <DefRow label="Auto-create work orders from PM" help="Raise the work order automatically when preventative maintenance falls due.">
                      <Switch checked={notificationSettings.auto_create_work_orders_from_pm || false} onCheckedChange={(c) => setPref('auto_create_work_orders_from_pm', c)} data-testid="notif-auto-wo-switch" />
                    </DefRow>
                    <DefRow label="New defect alerts" help="When a defect is reported from the web app, the operator app or a public QR page.">
                      <Switch checked={notificationSettings.defect_alerts ?? true} onCheckedChange={(c) => setPref('defect_alerts', c)} data-testid="notif-defect-alerts-switch" />
                    </DefRow>
                    <DefRow label="Inspection failure alerts" help="When an inspection records a defect or is marked failed.">
                      <Switch checked={notificationSettings.inspection_failures ?? true} onCheckedChange={(c) => setPref('inspection_failures', c)} data-testid="notif-inspection-failures-switch" />
                    </DefRow>
                    <DefRow label="GPS silence alerts" help="When a connected telematics device stops reporting for 24 hours or more.">
                      <Switch checked={notificationSettings.gps_silence_alerts ?? true} onCheckedChange={(c) => setPref('gps_silence_alerts', c)} data-testid="notif-gps-silence-switch" />
                    </DefRow>
                    <DefRow label="Certificate expiry emails" help="A daily email listing certificates that have expired or expire within 30 days. Off unless you switch it on." last>
                      <Switch checked={notificationSettings.certificate_alerts || false} onCheckedChange={(c) => setPref('certificate_alerts', c)} data-testid="notif-certificate-alerts-switch" />
                    </DefRow>
                  </>
                )}
              </SCard>

              <SCard
                title={<span className="flex items-center gap-2">Email delivery <span className="inline-flex items-center gap-1" style={{ fontSize: 11, fontWeight: 600, color: '#3D7A2E', background: '#EAF4E5', border: '1px solid #CDE6C2', padding: '1px 8px', borderRadius: 999 }}><span style={{ width: 6, height: 6, borderRadius: 999, background: '#3D7A2E' }} />Connected</span> <span style={{ fontSize: 12, color: '#8F8A7F', fontWeight: 400 }}>via Resend</span></span>}
                secondary
                footer={<div className="ml-auto"><button className={primaryBtn} onClick={handleSaveNotificationSettings} disabled={savingNotifications} data-testid="save-recipients-btn">{savingNotifications ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}Save recipients</button></div>}
              >
                {notificationSettings && (
                  <div style={{ padding: 16 }} className="space-y-4">
                    <div>
                      <FieldLabel>Recipients</FieldLabel>
                      <Input placeholder="email@company.com, manager@company.com" value={notificationSettings.email_recipients?.join(', ') || ''} onChange={(e) => setNotificationSettings({ ...notificationSettings, email_recipients: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} data-testid="email-recipients-input" />
                      <p style={{ fontSize: 12, color: '#6B6660', marginTop: 6 }}>Comma-separated. Leave empty to notify every admin user.</p>
                    </div>
                    <div>
                      <FieldLabel>Send a test</FieldLabel>
                      <div className="flex gap-2">
                        <Input placeholder="your@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="flex-1" data-testid="test-email-input" />
                        <button className={secondaryBtn} onClick={handleSendTestEmail} disabled={sendingTestEmail || !testEmail.trim()} data-testid="send-test-email-btn">{sendingTestEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}Send test</button>
                      </div>
                    </div>
                  </div>
                )}
              </SCard>
            </>
          )}

          {/* ── ERROR CODES ── */}
          {activeTab === 'error-codes' && (
            <div data-testid="error-codes-tab-content"><ErrorCodeLibrariesCard onChanged={loadErrorCodeCount} /></div>
          )}

          {/* ── READINESS ── */}
          {activeTab === 'readiness' && (
            <div data-testid="readiness-tab-content"><ReadinessSettings /></div>
          )}

          {activeTab === 'security' && (
            <div data-testid="security-tab-content"><SecurityActivityCard /></div>
          )}

          {/* ── SYSTEM ── */}
          {activeTab === 'system' && (
            <SCard title="QR codes" desc="Asset QR codes contain a full URL, so any phone camera opens the asset without a scanner app.">
              <div data-testid="public-qr-toggle-section">
                <DefRow label="Public asset QR access" help="Anyone who scans a code can see status, last inspection result and open defect count, and can report an issue — without logging in. Intended for hired-out equipment." last={!publicQrEnabled}>
                  <Switch checked={publicQrEnabled} onCheckedChange={togglePublicQr} disabled={publicQrLoading} data-testid="public-qr-toggle" />
                </DefRow>
              </div>
              {publicQrEnabled && (
                <div className="flex items-center justify-between gap-3 flex-wrap" style={{ background: '#FFFCEF', borderBottom: '1px solid #F0EDE6', padding: '12px 16px' }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#8F8A7F' }}>Live at</span>
                    <span className="font-mono truncate" style={{ fontSize: 12, color: '#1A1A1A', overflowWrap: 'anywhere' }}>{scanUrl}</span>
                  </div>
                  <button className="text-[#8A6D00] hover:text-[#6B5500] shrink-0" style={{ fontSize: 12.5, fontWeight: 600 }} onClick={() => window.open(scanBase + '/scan/', '_blank')} data-testid="preview-public-page">Preview the public page</button>
                </div>
              )}
              <DefRow label="Regenerate all QR codes" help="Reissues every asset code in the current URL format. Codes already printed keep working." last>
                <button className={secondaryBtn} onClick={handleRegenerateQRCodes} disabled={regeneratingQR} data-testid="regenerate-qr-btn">{regeneratingQR ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Regenerate</button>
              </DefRow>
            </SCard>
          )}
        </div>
      </div>
      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};
